import type { Transition } from 'framer-motion';

// Per-tab enter/exit animation — the same "sweep" as the Sweep intro: on tab
// change the AIS UNITED accent frame EXTENDS to full container height (covering
// SOLO) while its content fades out; the content swaps under cover; then the
// frame RETRACTS to the new tab's rest height as the content fades + rises back
// in. SOLO fades out under the cover and fades back in as it's revealed.
//
// (The whole-component intro animation is separate — see chatteaser3Intro.ts.)

export const CHATTEASER3_ANIM_PHASE = {
  Idle: 'idle',
  Exit: 'exit',
  Enter: 'enter',
} as const;

interface AnimPresetLocal {
  expandTransition: Transition;
  retractTransition: Transition;
  contentRiseY: number;
  contentOutTransition: Transition;
  contentInTransition: Transition;
  soloFadeOutTransition: Transition;
  soloFadeInTransition: Transition;
  exitMs: number;
  enterMs: number;
}

interface ContentFadePresetLocal {
  contentOutTransition: Transition;
  contentInTransition: Transition;
  soloFadeOutTransition: Transition;
  soloFadeInTransition: Transition;
}

const CONTENT_FADE_EXIT_EASE = [0.4, 0, 1, 1] as const;
const CONTENT_FADE_ENTER_EASE = [0.33, 0, 0.2, 1] as const;
const DEFAULT_UNITED_CONTENT_FADE_OUT_SECONDS = 0.14;
const DEFAULT_UNITED_CONTENT_FADE_IN_SECONDS = 0.55;
const DEFAULT_SOLO_CONTENT_FADE_OUT_SECONDS = 0.16;
const DEFAULT_SOLO_CONTENT_FADE_IN_SECONDS = 0.5;
const DEFAULT_SOLO_CONTENT_FADE_IN_DELAY_SECONDS = 0.14;
const UNITED_CONTENT_FADE_OUT_SECONDS = 0.08;
const UNITED_CONTENT_FADE_IN_SECONDS = 0.78;
const SOLO_CONTENT_FADE_OUT_SECONDS = 0.08;
const SOLO_CONTENT_FADE_IN_SECONDS = 0.75;
const SOLO_CONTENT_FADE_IN_DELAY_SECONDS = 0.16;

export const CHATTEASER3_CONTENT_FADE_PRESET = {
  Default: {
    contentOutTransition: {
      duration: DEFAULT_UNITED_CONTENT_FADE_OUT_SECONDS,
      ease: 'easeIn',
    },
    contentInTransition: {
      duration: DEFAULT_UNITED_CONTENT_FADE_IN_SECONDS,
      ease: 'easeOut',
    },
    soloFadeOutTransition: {
      duration: DEFAULT_SOLO_CONTENT_FADE_OUT_SECONDS,
      ease: 'easeIn',
    },
    soloFadeInTransition: {
      duration: DEFAULT_SOLO_CONTENT_FADE_IN_SECONDS,
      ease: 'easeOut',
      delay: DEFAULT_SOLO_CONTENT_FADE_IN_DELAY_SECONDS,
    },
  },
  Mobile: {
    contentOutTransition: {
      duration: UNITED_CONTENT_FADE_OUT_SECONDS,
      ease: CONTENT_FADE_EXIT_EASE,
    },
    contentInTransition: {
      duration: UNITED_CONTENT_FADE_IN_SECONDS,
      ease: CONTENT_FADE_ENTER_EASE,
    },
    soloFadeOutTransition: {
      duration: SOLO_CONTENT_FADE_OUT_SECONDS,
      ease: CONTENT_FADE_EXIT_EASE,
    },
    soloFadeInTransition: {
      duration: SOLO_CONTENT_FADE_IN_SECONDS,
      ease: CONTENT_FADE_ENTER_EASE,
      delay: SOLO_CONTENT_FADE_IN_DELAY_SECONDS,
    },
  },
} as const satisfies Record<string, ContentFadePresetLocal>;

export const TAB_ANIM_PRESET: AnimPresetLocal = {
  // The extend-to-full ("up") is quicker now.
  expandTransition: { duration: 0.3, ease: 'easeOut' },
  retractTransition: { duration: 0.52, ease: 'easeOut' },
  contentRiseY: 26,
  ...CHATTEASER3_CONTENT_FADE_PRESET.Default,
  exitMs: 360,
  enterMs: 740,
};
